<template>
  <div>
    <!-- Alert: No item found -->
    <b-alert :show="bankData === undefined" variant="danger">
      <h4 class="alert-heading">
        Erreur de récupération des données de la Banque
      </h4>
      <div class="alert-body">
        Pas de banque trouvée avec les informations. Vérifiez
        <b-link :to="{ name: 'apps-banks-list' }" class="alert-link">
          la liste
        </b-link>
        pour les autres banques.
      </div>
    </b-alert>

    <template v-if="bankData">
      <b-row>
        <b-col cols="12" lg="8" md="7" xl="9">
          <bank-view-bank-info-card :bank-data="bankData" @operation-done="triggerFetch" />
        </b-col>
        <b-col cols="12" lg="4" md="5" xl="3">
          <bank-view-bank-plan-card :bank-data="bankData" />
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <custom-list
            ref="bankHistoryListTableRef"
            concernedList="bankHistory"
            :items="fetchBankHistories"
            :total="totalFundHistories"
            :fields="bankHistoryFields"
            :perPage.sync="perPage"
            :pageOptions="pageOptions"
            :currentPage.sync="currentPage"
            :sortBy.sync="sortBy"
            :sortDesc.sync="sortDesc"
            :sortDirection="sortDirection"
            :filter.sync="filter"
          />
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import store from "@/store";
import router from "@/router";
import { computed, ref, watch } from "@vue/composition-api";
import { BAlert, BCol, BLink, BRow, BTab, BTabs } from "bootstrap-vue";
import BankViewBankInfoCard from "./BankViewBankInfoCard.vue";
import BankViewBankPlanCard from "./BankViewBankPlanCard.vue";
import CustomList from "@/components/fund-history/customList.vue";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useToast } from "vue-toastification/composition";
import {status} from "@/utils/constants/httpStatusCode";

export default {
  components: {
    BRow,
    BCol,
    BAlert,
    BLink,

    // Local Components
    BankViewBankInfoCard,
    BankViewBankPlanCard,
    CustomList,
  },
  setup() {
    const toast = useToast();
    const bankData = ref(null);
    const bankHistoryListTableRef = ref(null);
    const perPage = ref(5);
    const currentPage = ref(1);
    const pageOptions = ref([5, 10, 15, 20]);
    const filter = ref("");
    const sortBy = ref("created_at");
    const sortDesc = ref(false);
    const sortDirection = ref("asc");
    const bankHistoryFields = [
      {
        key: "id",
        label: "Id",
      },
      { key: "label", label: "Désignation", sortable: true },
      { key: "debit", label: "Sortie", sortable: true },
      { key: "credit", label: "Entrée", sortable: true },
      { key: "balance", label: "Solde après Opération" },
      { key: "type", label: "Type" },
      { key: "made_by", label: "Effectuée Par", sortable: true },
      { key: "created_at", label: "Date", sortable: true },
    ];

    const bankFundHistories = computed(() => {
      return store.state.fundHistory.sub_cr_fund_histories;
    });

    const totalFundHistories = computed(
      () => store.getters["fundHistory/totalSubCRFundHistories"]
    );

    const getErrors = computed(() => store.getters["fundHistory/errors"]);

    watch([currentPage, perPage], () => {
      refetchData();
    });

    watch(getErrors, (newVal) => {
      if (newVal) {
        let variant = newVal.code !== status.UNPROCESSABLE_ENTITY ? "danger" : "warning";

        showToast(newVal.message, newVal.error, "XIcon", variant);
      }
      resetError();
    });

    const showToast = (message, content, icon, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: icon,
          variant: variant,
          text: content,
        },
      });
    };

    const resetError = () => store.commit("fundHistory/SET_ERRORS", null);

    const refetchData = () => {
      bankHistoryListTableRef.value.$refs.refListTable.refresh();
    };

    const fetchBankData = () => {
      store
        .dispatch("bank/fetchBank", { id: router.currentRoute.params.id })
        .then((response) => {
          bankData.value = response;
        })
        .catch((error) => {
          if (error.response.status === 404) {
            bankData.value = undefined;
          }
        });
    };

    const fetchBankHistories = (ctx, callback) => {
      const params = {
        perPage: ctx.perPage,
        page: ctx.currentPage,
        sortBy: ctx.sortBy,
        sortDesc: ctx.sortDesc,
        filter: ctx.filter,
      };
      store
        .dispatch("fundHistory/fetchSubCashRegisterFundHistories", {
          params,
          sub_cr_id: router.currentRoute.params.id,
        })
        .then(() => {
          callback(bankFundHistories.value);
        });
    };

    const triggerFetch = () => {
      fetchBankData();
      refetchData();
    };

    fetchBankData();

    return {
      fetchBankHistories,
      triggerFetch,
      bankHistoryListTableRef,
      perPage,
      currentPage,
      pageOptions,
      filter,
      sortBy,
      sortDesc,
      sortDirection,
      bankHistoryFields,
      totalFundHistories,
      bankData,
    };
  },
};
</script>

<style></style>
